import { useEffect, useMemo, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MessageSquare, Bug, Sparkles, ThumbsUp, Wrench, Inbox, Reply, Trash2, Send } from "lucide-react";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";

const CAT = {
  feature: { l: "Feature request", icon: Sparkles, color: "bg-primary/10 text-primary border-primary/30" },
  bug: { l: "Glitch / bug", icon: Bug, color: "bg-rose-50 text-rose-700 border-rose-200" },
  improvement: { l: "Improvement", icon: Wrench, color: "bg-amber-50 text-amber-800 border-amber-200" },
  praise: { l: "Praise", icon: ThumbsUp, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  other: { l: "Other", icon: MessageSquare, color: "bg-zinc-50 text-zinc-700 border-zinc-200" },
};

const STATUS = {
  open: { l: "Open", color: "bg-blue-50 text-blue-700 border-blue-200" },
  in_progress: { l: "In progress", color: "bg-amber-50 text-amber-800 border-amber-200" },
  resolved: { l: "Resolved", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  wont_do: { l: "Won't do", color: "bg-zinc-50 text-zinc-700 border-zinc-200" },
};

export default function Feedback() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterUser, setFilterUser] = useState("");
  const [adminUsers, setAdminUsers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/feedback", {
        params: {
          category: filterCat || undefined,
          status: filterStatus || undefined,
          submitted_by: filterUser || undefined,
        },
      });
      setRows(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCat, filterStatus, filterUser]);
  const { indicator: pullIndicator } = usePullToRefresh(load);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users").then((r) => setAdminUsers((r.data || []).filter((u) => u.is_active))).catch(() => {});
  }, [isAdmin]);

  const groupedByUser = useMemo(() => {
    if (!isAdmin) return null;
    const m = new Map();
    rows.forEach((r) => {
      const k = r.submitted_by;
      if (!m.has(k)) m.set(k, { name: r.submitted_by_name, email: r.submitted_by_email, items: [] });
      m.get(k).items.push(r);
    });
    return Array.from(m.entries()).map(([uid, v]) => ({ uid, ...v }));
  }, [rows, isAdmin]);

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1100px] mx-auto">
      {pullIndicator}

      <header className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Feedback</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">
          {isAdmin ? "Inbox." : "Tell us anything."}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          {isAdmin
            ? "Suggestions, bug reports and feature requests from your team — grouped by who sent them."
            : "Spotted a glitch? Have an idea that would save you time? Drop it here — we read every one."}
        </p>
      </header>

      {!isAdmin && <FeedbackForm onSubmitted={load} />}

      {/* Filter bar — admin only */}
      {isAdmin && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
          <Select value={filterCat || "__all"} onValueChange={(v) => setFilterCat(v === "__all" ? "" : v)}>
            <SelectTrigger className="rounded-2xl h-11" data-testid="fb-filter-cat"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent className="rounded-2xl">
              <SelectItem value="__all">All categories</SelectItem>
              {Object.entries(CAT).map(([k, v]) => <SelectItem key={k} value={k}>{v.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus || "__all"} onValueChange={(v) => setFilterStatus(v === "__all" ? "" : v)}>
            <SelectTrigger className="rounded-2xl h-11" data-testid="fb-filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent className="rounded-2xl">
              <SelectItem value="__all">All statuses</SelectItem>
              {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterUser || "__all"} onValueChange={(v) => setFilterUser(v === "__all" ? "" : v)}>
            <SelectTrigger className="rounded-2xl h-11 col-span-2 sm:col-span-1" data-testid="fb-filter-user"><SelectValue placeholder="All employees" /></SelectTrigger>
            <SelectContent className="rounded-2xl">
              <SelectItem value="__all">All employees</SelectItem>
              {adminUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <Card className="rounded-3xl border-border/60 shadow-soft mt-5">
        <CardContent className="p-3 space-y-3">
          {loading ? <Skeleton className="h-32 rounded-2xl" /> : rows.length === 0 ? (
            <div className="py-14 text-center">
              <Inbox className="h-7 w-7 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">{isAdmin ? "No feedback yet — your team will start dropping ideas here." : "You haven't submitted any feedback yet. The form above is your direct line."}</p>
            </div>
          ) : isAdmin && groupedByUser ? (
            groupedByUser.map((g) => (
              <div key={g.uid} className="rounded-2xl border border-border/60 overflow-hidden" data-testid={`fb-group-${g.uid}`}>
                <div className="px-4 py-2.5 bg-muted/40 border-b border-border/60 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{g.name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{g.email}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px] font-bold">{g.items.length}</Badge>
                </div>
                <div className="divide-y divide-border/60">
                  {g.items.map((f) => <FeedbackItem key={f.id} f={f} isAdmin onChanged={load} />)}
                </div>
              </div>
            ))
          ) : (
            rows.map((f) => <FeedbackItem key={f.id} f={f} isAdmin={false} onChanged={load} />)
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function FeedbackForm({ onSubmitted }) {
  const [category, setCategory] = useState("feature");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Give it a short title"); return; }
    setSaving(true);
    try {
      await api.post("/feedback", {
        category,
        title: title.trim(),
        body: body.trim(),
        severity: category === "bug" ? severity : null,
      });
      toast.success("Sent · thank you. We'll get back to you.");
      setTitle(""); setBody("");
      onSubmitted?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  return (
    <Card className="rounded-3xl border-border/60 shadow-soft" data-testid="feedback-form">
      <CardContent className="p-5 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="rounded-2xl h-11" data-testid="fb-category"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-2xl">
              {Object.entries(CAT).map(([k, v]) => <SelectItem key={k} value={k}>{v.l}</SelectItem>)}
            </SelectContent>
          </Select>
          {category === "bug" ? (
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger className="rounded-2xl h-11" data-testid="fb-severity"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                <SelectItem value="low">Low — minor annoyance</SelectItem>
                <SelectItem value="medium">Medium — slows me down</SelectItem>
                <SelectItem value="high">High — blocks my work</SelectItem>
              </SelectContent>
            </Select>
          ) : <div />}
        </div>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="One-line summary (e.g. 'Add WhatsApp template for AMC renewal')"
          className="rounded-2xl h-12"
          maxLength={200}
          data-testid="fb-title"
        />
        <Textarea
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Tell us more — what would make this useful, where you ran into it, or what you'd want it to do."
          className="rounded-2xl"
          maxLength={5000}
          data-testid="fb-body"
        />
        <Button onClick={submit} disabled={saving || !title.trim()} className="w-full h-11 rounded-2xl tap" data-testid="fb-submit">
          <Send className="h-4 w-4 mr-1.5" /> {saving ? "Sending…" : "Send to admin"}
        </Button>
      </CardContent>
    </Card>
  );
}

function FeedbackItem({ f, isAdmin, onChanged }) {
  const cat = CAT[f.category] || CAT.other;
  const [reply, setReply] = useState(f.admin_reply || "");
  const [replyOpen, setReplyOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const Icon = cat.icon;

  const setStatus = async (status) => {
    setBusy(true);
    try { await api.patch(`/feedback/${f.id}`, { status }); toast.success(`Marked ${STATUS[status]?.l}`); onChanged?.(); }
    catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };

  const saveReply = async () => {
    setBusy(true);
    try { await api.patch(`/feedback/${f.id}`, { admin_reply: reply }); toast.success("Reply saved"); setReplyOpen(false); onChanged?.(); }
    catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this feedback?")) return;
    try { await api.delete(`/feedback/${f.id}`); toast.success("Deleted"); onChanged?.(); }
    catch (err) { toast.error(fmtError(err)); }
  };

  return (
    <div className="p-4" data-testid={`fb-item-${f.id}`}>
      <div className="flex items-start gap-3">
        <span className={`inline-grid place-items-center h-9 w-9 rounded-2xl border ${cat.color} shrink-0`}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <Badge variant="outline" className={`text-[10px] font-bold ${cat.color}`}>{cat.l}</Badge>
            <Badge variant="outline" className={`text-[10px] font-bold ${STATUS[f.status]?.color}`}>{STATUS[f.status]?.l}</Badge>
            {f.severity && f.category === "bug" && (
              <Badge variant="outline" className="text-[10px] font-bold capitalize">{f.severity}</Badge>
            )}
            <span className="text-[10px] text-muted-foreground ml-auto whitespace-nowrap">{new Date(f.created_at).toLocaleDateString()}</span>
          </div>
          <p className="text-sm font-semibold leading-snug">{f.title}</p>
          {f.body && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap break-words">{f.body}</p>}

          {/* Admin reply (visible to both) */}
          {f.admin_reply && (
            <div className="mt-2 rounded-xl bg-primary/5 border border-primary/20 p-2.5">
              <p className="text-[10px] uppercase tracking-wider font-bold text-primary inline-flex items-center gap-1"><Reply className="h-2.5 w-2.5" /> Admin reply</p>
              <p className="text-xs mt-1 whitespace-pre-wrap">{f.admin_reply}</p>
              {f.replied_by && <p className="text-[10px] text-muted-foreground mt-1">— {f.replied_by}{f.replied_at ? ` · ${new Date(f.replied_at).toLocaleDateString()}` : ""}</p>}
            </div>
          )}

          {/* Admin actions */}
          {isAdmin && (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              <Select value={f.status} onValueChange={setStatus}>
                <SelectTrigger className="rounded-xl h-8 text-[11px] w-32" data-testid={`fb-status-${f.id}`}><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => setReplyOpen((o) => !o)} className="rounded-xl h-8 text-[11px]" data-testid={`fb-reply-toggle-${f.id}`}>
                <Reply className="h-3 w-3 mr-1" /> {replyOpen ? "Cancel" : f.admin_reply ? "Edit reply" : "Reply"}
              </Button>
              <button onClick={remove} disabled={busy} className="h-8 w-8 grid place-items-center rounded-xl hover:bg-red-50 text-muted-foreground hover:text-red-600 tap" title="Delete" data-testid={`fb-delete-${f.id}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          )}

          {isAdmin && replyOpen && (
            <div className="mt-2 space-y-2">
              <Textarea
                rows={2}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Reply to the team member…"
                className="rounded-xl text-xs"
                data-testid={`fb-reply-text-${f.id}`}
              />
              <Button onClick={saveReply} disabled={busy} className="rounded-xl h-8 text-[11px]" data-testid={`fb-reply-save-${f.id}`}>
                <Send className="h-3 w-3 mr-1" /> Save reply
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
